import { ReactNode } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@design-system';
import { DAYS_IN_MONTH } from '@shared/calculations/funding/projection/sharedConstants';
import {
  getLtcPool,
  getMonthPayout,
} from '@shared/calculations/funding/projection/projectionTotals';
import { carePhaseLabel, careEnvironmentLabel } from '../../glossary/enumLabels';
import { currency, monthDisplay } from './formatters';
import { milestoneBadgeClass } from './milestoneColors';
import type { StrategyPresentation } from './strategies/types';

const rangeLabel = (start: string | number, end: string | number) =>
  start === end ? String(start) : `${start}–${end}`;

export function WhenTooltipContent({
  startMonth,
  endMonth,
}: {
  startMonth: MonthlyEntry;
  endMonth?: MonthlyEntry;
}) {
  const end = endMonth ?? startMonth;
  const ageText =
    startMonth.context.clientAge != null && end.context.clientAge != null
      ? rangeLabel(startMonth.context.clientAge, end.context.clientAge)
      : '—';
  const policyYearText = rangeLabel(startMonth.context.policyYear, end.context.policyYear);
  const startPhase = `${carePhaseLabel(startMonth.context.carePhase)} at ${careEnvironmentLabel(startMonth.context.careEnvironment)}`;
  const endPhase = `${carePhaseLabel(end.context.carePhase)} at ${careEnvironmentLabel(end.context.careEnvironment)}`;
  const phaseText = startPhase === endPhase ? startPhase : `${startPhase} → ${endPhase}`;

  return (
    <TooltipContent className="text-xs">
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
        <span className="text-gray-300">Age</span>
        <span className="font-semibold">{ageText}</span>
        <span className="text-gray-300">Policy Year</span>
        <span className="font-semibold">{policyYearText}</span>
        <span className="text-gray-300">Care Phase</span>
        <span className="font-semibold">{phaseText}</span>
      </div>
    </TooltipContent>
  );
}

export function WhenCell({ month }: { month: MonthlyEntry }) {
  const milestones = month.events.milestones;

  return (
    <div className="flex flex-col gap-1.5 max-w-28">
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-block text-sm font-semibold leading-snug text-gray-900 cursor-help">
            Month {monthDisplay(month.context.monthIndex)}
          </span>
        </TooltipTrigger>
        <WhenTooltipContent startMonth={month} />
      </Tooltip>
      {milestones.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {milestones.map((m, mi) => (
            <span
              key={mi}
              className={`rounded px-2 py-0.5 text-xs font-medium whitespace-nowrap ${milestoneBadgeClass(m.kind)}`}
            >
              {m.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export function CareCell({ month }: { month: MonthlyEntry }) {
  return (
    <div className="text-xs text-gray-800">
      <span className="font-medium">{carePhaseLabel(month.context.carePhase)}</span>
      <span className="mx-1 text-gray-400">/</span>
      <span className="text-gray-600">{careEnvironmentLabel(month.context.careEnvironment)}</span>
    </div>
  );
}

export function CostCell({
  month,
  terms,
}: {
  month: MonthlyEntry;
  terms: ClaimTerms;
}) {
  const phaseStartYear =
    terms.phaseStartYears[month.context.carePhase] ?? terms.projectionAnchorYear;
  const yearsFromTodayContinuous =
    phaseStartYear - terms.projectionAnchorYear + month.context.monthsIntoPhase / 12;
  const yearsFromToday = Math.max(Math.floor(yearsFromTodayContinuous), 0);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="leading-tight cursor-help">
          <div className="font-medium text-gray-900">{currency(month.facts.effectiveCareCost)}</div>
          <div className="text-[10px] text-gray-500">
            {currency(month.facts.dailyCareCost)}/day
          </div>
        </div>
      </TooltipTrigger>
      <InflationTooltipContent
        startingLabel="Today's monthly cost"
        startingValue={month.facts.nominalCareCost}
        rate={terms.careCostInflationRate}
        years={yearsFromToday}
        result={month.facts.effectiveCareCost}
      />
    </Tooltip>
  );
}

export function BenefitAmountCell({
  snapshot,
  terms,
  presentation,
}: {
  snapshot: YearSnapshot;
  terms: ClaimTerms;
  presentation?: StrategyPresentation;
}) {
  const baseMonthly = terms.nominalDailyBenefitBase * DAYS_IN_MONTH;
  const intYear = Math.floor(snapshot.policyYear);
  const cap = terms.inflationCapYears > 0 ? terms.inflationCapYears : null;
  const effectiveYear = cap !== null && intYear > cap ? cap : intYear;
  const increments = Math.max(effectiveYear - 1, 0);
  const capped = cap !== null && intYear > cap;
  const note = capped
    ? (presentation?.copy?.benefitInflationCapNote?.(cap) ?? `Inflation capped at year ${cap}`)
    : undefined;
  const isSimple = terms.inflationIsSimple === true;
  // Strategy "compound" label copy only applies when actually compounding.
  // Simple-form CM II falls back to a neutral label so a "Compound rider
  // growth" override doesn't mislabel linear growth.
  const growthLabel = isSimple
    ? 'Simple growth'
    : presentation?.copy?.benefitInflationCompoundLabel ?? 'Compound growth';
  const footer =
    presentation?.benefitAmountCellTooltipFooter?.({ snapshot, terms }) ?? null;
  // Headline = the per-month benefit ceiling this year. For CM II that's
  // baseMonthly + riderMonthly; for Asset Flex it's the ACIO-inflated
  // monthly cap. Both products store this on `monthlyBenefitCap`. The
  // tooltip formula uses the strategy's per-product rate (Asset Flex's
  // On-Face ACIO can differ from policy-wide ACIO) and the simple-vs-
  // compound mode flagged in `terms.inflationIsSimple`.
  const rate = presentation?.benefitInflationRate?.(terms) ?? terms.inflationRate;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="leading-tight cursor-help">
          <div className="font-medium text-gray-900">
            {currency(snapshot.monthlyBenefitCap)}
          </div>
          <div className="text-[10px] text-gray-500">
            {currency(snapshot.effectiveDailyBenefit)}/day
          </div>
        </div>
      </TooltipTrigger>
      <InflationTooltipContent
        header={`Policy year ${intYear}`}
        startingLabel="Base monthly"
        startingValue={baseMonthly}
        rate={rate}
        years={increments}
        result={snapshot.monthlyBenefitCap}
        note={note}
        compoundLabel={growthLabel}
        isSimple={isSimple}
        footer={footer}
      />
    </Tooltip>
  );
}

function InflationTooltipContent({
  header,
  startingLabel,
  startingValue,
  rate,
  years,
  result,
  note,
  compoundLabel = 'Compound growth',
  isSimple = false,
  footer,
}: {
  header?: string;
  startingLabel: string;
  startingValue: number;
  rate: number;
  years: number;
  result: number;
  note?: string;
  compoundLabel?: string;
  isSimple?: boolean;
  footer?: ReactNode;
}) {
  const ratePct = (rate * 100).toFixed(2);
  return (
    <TooltipContent className="text-xs">
      {header && <div className="mb-1 font-semibold">{header}</div>}
      <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
        <span className="text-gray-300">{startingLabel}</span>
        <span className="font-semibold tabular-nums">{currency(startingValue)}</span>
        <span className="text-gray-300">{compoundLabel}</span>
        <span className="font-semibold tabular-nums">
          {isSimple ? (
            <>× (1 + {ratePct}% × {years})</>
          ) : (
            <>× (1 + {ratePct}%)<sup>{years}</sup></>
          )}
        </span>
        <span className="text-gray-300">Result</span>
        <span className="font-semibold tabular-nums">{currency(result)}</span>
        {note && (
          <>
            <span />
            <span className="text-[10px] text-amber-300">{note}</span>
          </>
        )}
      </div>
      {footer && <div className="mt-2 border-t border-gray-700 pt-1">{footer}</div>}
    </TooltipContent>
  );
}

export function EligibilityCell({
  month,
  openState,
}: {
  month: MonthlyEntry;
  openState: MonthlyState;
}) {
  const eligible = month.facts.coverageEligibleDays;
  const non2Adl = month.facts.non2AdlDays;
  const wpHome = month.facts.waitingPeriodDaysConsumedHome;
  const wpFacility = month.facts.waitingPeriodDaysConsumedFacility;
  // Home and facility WPs decrement equally — use either as the canonical count.
  const wpConsumed = Math.max(wpHome, wpFacility);
  const wpRemaining = Math.max(
    month.state.waitingPeriod.homeDays,
    month.state.waitingPeriod.facilityDays,
  );
  const ineligible = Math.max(DAYS_IN_MONTH - eligible, 0);

  const reasons: { label: string; tone: string }[] = [];
  if (non2Adl > 0) {
    reasons.push({ label: `${non2Adl}d <2 ADL`, tone: 'bg-amber-50 text-amber-800' });
  }
  if (wpConsumed > 0) {
    reasons.push({ label: `${wpConsumed}d in WP`, tone: 'bg-rose-50 text-rose-800' });
  }

  const wpWasActive =
    openState.waitingPeriod.homeDays > 0 || openState.waitingPeriod.facilityDays > 0;

  return (
    <div className="leading-tight">
      <div>
        <span className="text-sm font-semibold text-gray-900">{eligible}</span>
        <span className="mx-1 text-gray-500">/</span>
        <span className="text-xs text-gray-600">{ineligible} ineligible</span>
      </div>
      {reasons.length > 0 && (
        <div className="mt-0.5 flex flex-wrap gap-1">
          {reasons.map((r, i) => (
            <span
              key={i}
              className={`rounded px-1 py-0 text-[10px] font-medium ${r.tone}`}
            >
              {r.label}
            </span>
          ))}
        </div>
      )}
      {wpRemaining > 0 && (
        <div className="mt-0.5 text-[10px] text-gray-600">WP remaining: {wpRemaining}d</div>
      )}
      {wpWasActive && wpRemaining === 0 && wpConsumed > 0 && (
        <div className="mt-0.5 text-[10px] font-medium text-emerald-700">WP satisfied</div>
      )}
    </div>
  );
}

export function PaidCell({
  month,
  cumulativeToDate,
  presentation,
  terms,
}: {
  month: MonthlyEntry;
  cumulativeToDate?: number;
  presentation?: StrategyPresentation;
  terms: ClaimTerms;
}) {
  const headline = getMonthPayout(month);
  const extras = presentation?.paidCellExtras?.(month) ?? null;
  const tooltipContent = presentation?.payoutCellTooltip?.({ month, terms }) ?? null;

  if (headline === 0 && extras == null && tooltipContent == null) {
    return <span className="text-gray-300">—</span>;
  }

  const headlineNode = (
    <div className="font-medium text-gray-900">
      {currency(headline)}
      {cumulativeToDate !== undefined && (
        <span className="ml-1 font-normal text-gray-500">({currency(cumulativeToDate)})</span>
      )}
    </div>
  );

  return (
    <div className="leading-tight">
      {tooltipContent ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="cursor-help">{headlineNode}</div>
          </TooltipTrigger>
          {tooltipContent}
        </Tooltip>
      ) : (
        headlineNode
      )}
      {extras}
    </div>
  );
}

// Closing LTC pool + this-month delta. LTC pool is derived (base + eob)
// — read it via `getLtcPool`. Optional `tooltipContent` wraps in a Tooltip
// (used by strategies like Asset Flex to break out per-pool balances).
export function LtcPoolWithDeltaCell({
  openState,
  closeState,
  tooltipContent,
}: {
  openState: MonthlyState;
  closeState: MonthlyState;
  tooltipContent?: ReactNode;
}) {
  return (
    <BalanceWithDeltaCell
      opening={getLtcPool(openState)}
      closing={getLtcPool(closeState)}
      tooltipContent={tooltipContent}
    />
  );
}

// Closing balance + this-row delta (green = increase, rose = decrease).
// Closing is the headline (matches financial-statement convention and
// the month on which milestones like pool-exhausted fire); delta below
// reflects this month's change. Delta is omitted when zero. Optional
// `tooltipContent` wraps the cell in a Tooltip.
export function BalanceWithDeltaCell({
  opening,
  closing,
  tooltipContent,
}: {
  opening: number;
  closing: number;
  tooltipContent?: ReactNode;
}) {
  const delta = closing - opening;
  const inner = (
    <div className="leading-tight">
      <div className="font-medium text-gray-900">{currency(closing)}</div>
      {delta !== 0 && (
        <div className={`text-[10px] ${delta > 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
          {delta > 0 ? '+' : '−'}
          {currency(Math.abs(delta))}
        </div>
      )}
    </div>
  );
  if (!tooltipContent) return inner;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="cursor-help">{inner}</div>
      </TooltipTrigger>
      {tooltipContent}
    </Tooltip>
  );
}
